<template>
  <AuthLayout :mode="mode">
    <Section heading="Ethics Records" :mode="mode" />
    <AppCard :mode="mode">
      <template #body>
        <div class="grid gap-6 sm:gap-4">
          <Notes :list="notesList" />
          <div class="grid grid-cols-3 gap-3 sm:grid-cols-1">
            <AppInput
              :mode="mode"
              id="ethics-search"
              title="Search"
              placeholder="Search by company, name, email..."
              v-model="q"
              @input="onSearch"
            />
            <AppSelect
              :mode="mode"
              title="Category"
              v-model="filterCategory"
              @change="fetch"
            >
              <template #options>
                <option value="">All</option>
                <option value="violator">Violator</option>
                <option value="non_participant">Non-Participant</option>
                <option value="fraud_alert">Fraud Alert</option>
              </template>
            </AppSelect>
          </div>

          <div
            class="overflow-hidden rounded-xl border"
            :class="[
              mode === 'dark'
                ? 'border-dark-border bg-dark-sidebar'
                : 'border-border-10 bg-white-100',
            ]"
          >
            <div class="without-scrollbar overflow-x-auto">
              <table
                class="table"
                :class="[mode === 'dark' ? 'dark-table' : '']"
              >
                <thead>
                  <tr>
                    <th
                      v-for="col in columns"
                      :key="col.key"
                      class="cursor-pointer select-none"
                      @click="toggleSort(col.key)"
                    >
                      {{ col.label }}
                      <span
                        v-if="sortKey === col.key"
                        class="ml-1 text-primary-100"
                        >{{ sortDir === "asc" ? "▲" : "▼" }}</span
                      >
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, key) in paginatedItems" :key="key">
                    <td class="align-top">
                      <PartyCell :party="row.respondent" :mode="mode" />
                    </td>
                    <td class="align-top">
                      <PartyCell :party="row.complainant" :mode="mode" />
                    </td>
                    <td class="align-top">
                      <span class="text-[14px] font-medium">{{
                        row.date_display
                      }}</span>
                    </td>
                    <td class="align-top">
                      <CategoryBadge :category="row.category" />
                    </td>
                  </tr>
                  <tr v-if="sortedRecords.length === 0">
                    <td colspan="4" class="p-4 text-center text-black-60">
                      No records.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <AppPagination
            :mode="mode"
            :data="sortedRecords"
            @pageChanged="updatePaginatedItems"
          />
        </div>
      </template>
    </AppCard>
  </AuthLayout>
</template>

<script>
import ApiRequest from "@L/ApiRequest";
import Section from "@C/Common/Section.vue";
import Notes from "@C/Common/Notes.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppPagination from "@C/Common/AppPagination.vue";
import AppInput from "@C/Form/AppInput.vue";
import AppSelect from "@C/Form/AppSelect.vue";
import AuthLayout from "@A/AuthLayout.vue";
import PartyCell from "@C/Ethics/PartyCell.vue";
import CategoryBadge from "@C/Ethics/CategoryBadge.vue";

const COLUMNS = [
  { key: "respondent", label: "Respondent" },
  { key: "complainant", label: "Complainant" },
  { key: "date", label: "Date" },
  { key: "category", label: "Category" },
];

const CATEGORY_LABEL = {
  violator: "Violator",
  non_participant: "Non-Participant",
  fraud_alert: "Fraud Alert",
};

function sortValue(row, key) {
  if (key === "respondent")
    return (row.respondent && row.respondent.company) || "";
  if (key === "complainant")
    return (row.complainant && row.complainant.company) || "";
  if (key === "date") return row.date || "";
  if (key === "category") return CATEGORY_LABEL[row.category] || row.category || "";
  return "";
}

export default {
  name: "EthicsRecords",
  components: {
    AuthLayout,
    Section,
    Notes,
    AppCard,
    AppPagination,
    AppInput,
    AppSelect,
    PartyCell,
    CategoryBadge,
  },
  props: { mode: String },
  data() {
    return {
      q: "",
      debounce: null,
      filterCategory: "",
      records: [],
      paginatedItems: [],
      sortKey: "respondent",
      sortDir: "asc",
      columns: COLUMNS,
      notesList: [
        "Ethics Records include companies found in violation of the ITAD Association Code of Ethics, companies that have chosen not to participate in the ethics process, and fraud alerts.",
        "Click any column heading to sort.",
      ],
    };
  },
  computed: {
    sortedRecords() {
      const dir = this.sortDir === "asc" ? 1 : -1;
      const key = this.sortKey;
      return [...this.records].sort((a, b) => {
        const av = sortValue(a, key).toString().toLowerCase();
        const bv = sortValue(b, key).toString().toLowerCase();
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    async fetch() {
      const params = new URLSearchParams();
      if (this.filterCategory) params.set("category", this.filterCategory);
      if (this.q) params.set("q", this.q);
      try {
        const { data } = await ApiRequest().get(
          `/ethics_records?${params.toString()}`,
        );
        this.records = (data || []).map((d) => d.ethics_record);
      } catch (e) {
        this.records = [];
      }
    },
    onSearch() {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => this.fetch(), 300);
    },
    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortDir = "asc";
      }
    },
    updatePaginatedItems(items) {
      this.paginatedItems = items;
    },
  },
};
</script>
